import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useMotionValue, useSpring } from "framer-motion";

const BOT_W = 34;
const BOT_H = 46;

const SECTIONS = [
  { id: "manifesto", tip: "Our manifesto — why one AI workforce beats endless hiring.", side: "right", top: 0.35 },
  { id: "workforce", tip: "Thirteen AI employees, each a specialist. Let me walk you through them.", side: "left", top: 0.3 },
  { id: "capabilities", tip: "Everything the workforce can do, department by department.", side: "right", top: 0.4 },
  { id: "demo", tip: "Try it yourself — type an instruction and watch a department activate.", side: "left", top: 0.32 },
  { id: "why", tip: "Traditional business vs Alkyrvis — the honest maths.", side: "right", top: 0.35 },
  { id: "pricing", tip: "A full workforce for less than a single salary.", side: "left", top: 0.35 },
  { id: "start", tip: "Ready when you are — start building your workforce.", side: "right", top: 0.42 },
];

const AGENTS = [
  "Finance AI", "Marketing AI", "Sales AI", "Operations AI", "HR AI",
  "Customer Support AI", "Analytics AI", "Administration AI", "Scheduling AI",
  "Receptionist AI", "Voice Calling AI", "Website AI", "Automation AI",
];

const BotSvg = () => (
  <svg width={BOT_W} height={BOT_H} viewBox="0 0 60 80" fill="none" aria-hidden>
    <defs>
      <linearGradient id="gb-gold" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#F5E7C1" />
        <stop offset="55%" stopColor="#D8C08A" />
        <stop offset="100%" stopColor="#9A7B3F" />
      </linearGradient>
      <linearGradient id="gb-dark" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#9A7B3F" />
        <stop offset="100%" stopColor="#6B5426" />
      </linearGradient>
    </defs>
    <ellipse cx="30" cy="36" rx="17" ry="27" fill="#D8C08A" opacity="0.16" />
    <line x1="30" y1="1.5" x2="30" y2="7" stroke="#D8C08A" strokeWidth="1.4" />
    <circle cx="30" cy="2.2" r="1.7" fill="#4ADE80" />
    <circle cx="12.5" cy="26" r="4" fill="url(#gb-dark)" />
    <circle cx="47.5" cy="26" r="4" fill="url(#gb-dark)" />
    <rect x="9.5" y="29" width="6" height="13" rx="3" fill="url(#gb-gold)" />
    <rect x="44.5" y="29" width="6" height="13" rx="3" fill="url(#gb-gold)" />
    <circle cx="30" cy="14" r="9.5" fill="url(#gb-gold)" />
    <rect x="22.5" y="12" width="15" height="4.2" rx="2.1" fill="#050505" opacity="0.85" />
    <circle cx="26.5" cy="14.1" r="1.1" fill="#4ADE80" />
    <circle cx="33.5" cy="14.1" r="1.1" fill="#4ADE80" />
    <rect x="19" y="24" width="22" height="24" rx="7" fill="url(#gb-gold)" />
    <line x1="23" y1="29.5" x2="37" y2="29.5" stroke="#9A7B3F" strokeWidth="0.8" opacity="0.55" />
    <line x1="23" y1="43" x2="37" y2="43" stroke="#9A7B3F" strokeWidth="0.8" opacity="0.55" />
    <circle cx="30" cy="36" r="4.2" fill="#050505" opacity="0.85" />
    <circle cx="30" cy="36" r="2.3" fill="#F5E7C1" />
    <rect x="22" y="48" width="6.5" height="13" rx="3.25" fill="url(#gb-gold)" />
    <rect x="31.5" y="48" width="6.5" height="13" rx="3.25" fill="url(#gb-gold)" />
    <rect x="20.8" y="59" width="8.6" height="5" rx="2.5" fill="url(#gb-dark)" />
    <rect x="30.6" y="59" width="8.6" height="5" rx="2.5" fill="url(#gb-dark)" />
  </svg>
);

export default function GuideBot() {
  const [phase, setPhase] = useState("docked");
  const [sectionIdx, setSectionIdx] = useState(0);
  const [agentIdx, setAgentIdx] = useState(0);
  const [agentsDone, setAgentsDone] = useState(false);
  const [align, setAlign] = useState("left");
  const x = useMotionValue(-300);
  const y = useMotionValue(-300);
  const rot = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 62, damping: 16, mass: 1 });
  const sy = useSpring(y, { stiffness: 62, damping: 16, mass: 1 });
  const sRot = useSpring(rot, { stiffness: 95, damping: 17 });
  const sectionRef = useRef(0);
  const timers = useRef([]);
  const snakeRaf = useRef(null);
  const alignRef = useRef("left");
  const orbiting = useRef(false);
  const orbitRaf = useRef(null);
  const trailPts = useRef([]);
  const trailRef = useRef(null);
  const trailCoreRef = useRef(null);

  const later = (fn, ms) => {
    const t = setTimeout(fn, ms);
    timers.current.push(t);
  };

  useEffect(
    () => () => {
      timers.current.forEach(clearTimeout);
      if (snakeRaf.current) cancelAnimationFrame(snakeRaf.current);
    },
    []
  );

  const dockPoint = () => {
    const el = document.querySelector('[data-testid="hero-emblem"]');
    if (!el) return null;
    const r = el.getBoundingClientRect();
    return { x: r.left + r.width / 2 - BOT_W / 2, y: r.top - BOT_H + 10 };
  };

  const anchorFor = (sec) => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    if (vw < 768) {
      return { x: vw - BOT_W - 16, y: vh - 230 };
    }
    return {
      x: sec.side === "right" ? vw - BOT_W - 30 : 30,
      y: Math.round(vh * (sec.top ?? 0.35)),
    };
  };

  // head-first rotation + smoke contrail, driven by actual movement
  useEffect(() => {
    let raf;
    let prev = { x: sx.get(), y: sy.get() };
    const tick = () => {
      const cx = sx.get();
      const cy = sy.get();
      const vx = cx - prev.x;
      const vy = cy - prev.y;
      prev = { x: cx, y: cy };
      const speed = Math.hypot(vx, vy) * 60;
      if (orbiting.current) {
        // rotation is driven precisely by the orbit loop
      } else if (speed > 70) {
        const ang = ((Math.atan2(vy, vx) * 180) / Math.PI + 90 + 540) % 360 - 180;
        rot.set(ang);
      } else {
        rot.set(0);
      }
      const isRight = cx > window.innerWidth * 0.55;
      if (isRight !== alignRef.current) {
        alignRef.current = isRight;
        setAlign(isRight ? "right" : "left");
      }
      const now = performance.now();
      const theta = (sRot.get() * Math.PI) / 180;
      const pivotX = cx + BOT_W / 2;
      const pivotY = cy + BOT_H * 0.16;
      const d = BOT_H * 0.84;
      const fx = pivotX - d * Math.sin(theta);
      const fy = pivotY + d * Math.cos(theta);
      if (speed > 25) {
        trailPts.current.push({ x: fx, y: fy, t: now });
      }
      const maxAge = speed > 25 ? (orbiting.current ? 2200 : 1100) : 240;
      while (trailPts.current.length && now - trailPts.current[0].t > maxAge) {
        trailPts.current.shift();
      }
      const pts = trailPts.current.map((p) => `${p.x},${p.y}`).join(" ");
      if (trailRef.current) trailRef.current.setAttribute("points", pts);
      if (trailCoreRef.current) trailCoreRef.current.setAttribute("points", pts);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // snake flight — head-first, curved path, optional loop midway
  const flyToSnake = (to, loop = false) => {
    const from = { x: sx.get(), y: sy.get() };
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const dist = Math.hypot(dx, dy);
    if (dist < 120) {
      x.set(to.x);
      y.set(to.y);
      return;
    }
    if (snakeRaf.current) cancelAnimationFrame(snakeRaf.current);
    const dur = Math.min(Math.max(dist / 700, 1.6), 4) * 1500;
    const amp = Math.min(dist * (loop ? 0.3 : 0.18), loop ? 130 : 90);
    const waves = loop ? 2.5 : 1.5 + Math.random() * 0.7;
    const nx = -dy / dist;
    const ny = dx / dist;
    const t0 = performance.now();
    const step = (now) => {
      const p = Math.min((now - t0) / dur, 1);
      const e = -(Math.cos(Math.PI * p) - 1) / 2;
      const env = Math.sin(p * Math.PI);
      const off = Math.sin(p * Math.PI * 2 * waves) * amp * env;
      x.set(from.x + dx * e + nx * off);
      y.set(from.y + dy * e + ny * off);
      if (p < 1) snakeRaf.current = requestAnimationFrame(step);
    };
    snakeRaf.current = requestAnimationFrame(step);
  };

  // ride the emblem while docked / prompting / quiet
  useEffect(() => {
    if (!["docked", "prompt", "quiet"].includes(phase)) return undefined;
    let raf;
    const track = () => {
      if (!orbiting.current) {
        const p = dockPoint();
        if (p) {
          x.set(p.x);
          y.set(p.y);
        }
      }
      raf = requestAnimationFrame(track);
    };
    raf = requestAnimationFrame(track);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  useEffect(() => {
    if (phase !== "docked") return undefined;
    const t = setTimeout(() => setPhase((p) => (p === "docked" ? "prompt" : p)), 2600);
    return () => clearTimeout(t);
  }, [phase]);

  // victory lap around the logo every 30s while quietly docked
  useEffect(() => {
    if (phase !== "quiet") return undefined;
    const orbit = () => {
      const el = document.querySelector('[data-testid="hero-emblem"]');
      if (!el || orbiting.current) return;
      const r0 = el.getBoundingClientRect();
      if (r0.bottom < -100 || r0.top > window.innerHeight + 100) return;
      orbiting.current = true;
      const t0 = performance.now();
      const dur = 4200;
      const step = (now) => {
        const r = el.getBoundingClientRect();
        const cx = r.left + r.width / 2;
        const cy = r.top + r.height / 2;
        const R = r.height / 2 + BOT_H + 12;
        const p = Math.min((now - t0) / dur, 1);
        const ang = -Math.PI / 2 + p * Math.PI * 2;
        x.set(cx + R * Math.cos(ang) - BOT_W / 2);
        y.set(cy + R * Math.sin(ang) - BOT_H / 2);
        rot.set((ang * 180) / Math.PI + 180);
        if (p < 1) {
          orbitRaf.current = requestAnimationFrame(step);
        } else {
          orbiting.current = false;
          rot.jump((((rot.get() + 180) % 360) + 360) % 360 - 180);
        }
      };
      orbitRaf.current = requestAnimationFrame(step);
    };
    const first = setTimeout(orbit, 8000);
    const every = setInterval(orbit, 30000);
    return () => {
      clearTimeout(first);
      clearInterval(every);
      if (orbitRaf.current) cancelAnimationFrame(orbitRaf.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // glide alongside the visitor during the tour
  useEffect(() => {
    if (phase !== "tour") return undefined;
    const detect = () => {
      const vh = window.innerHeight;
      let idx = sectionRef.current;
      SECTIONS.forEach((s, i) => {
        const el = document.getElementById(s.id);
        if (el && el.getBoundingClientRect().top <= vh * 0.45) idx = i;
      });
      const isNew = idx !== sectionRef.current;
      if (isNew) {
        sectionRef.current = idx;
        setSectionIdx(idx);
      }
      const el = document.getElementById(SECTIONS[idx].id);
      const base = anchorFor(SECTIONS[idx]);
      let targetY = base.y;
      let targetX = base.x;
      if (el) {
        const r = el.getBoundingClientRect();
        const prog = Math.min(Math.max((vh * 0.45 - r.top) / r.height, 0), 1);
        targetY = base.y + prog * 110;
        if (window.innerWidth >= 768) {
          targetX =
            base.x +
            Math.sin(prog * Math.PI) * (SECTIONS[idx].side === "right" ? -80 : 80);
        }
      }
      if (isNew) flyToSnake({ x: targetX, y: targetY });
      else {
        x.set(targetX);
        y.set(targetY);
      }
    };
    window.addEventListener("scroll", detect, { passive: true });
    detect();
    return () => window.removeEventListener("scroll", detect);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // end of tour
  useEffect(() => {
    if (phase !== "tour") return undefined;
    if (sectionRef.current === SECTIONS.length - 1 && agentsDone) {
      later(() => {
        setPhase("closing");
        later(() => {
          setPhase("return");
          const p = dockPoint();
          if (p) flyToSnake(p, true);
          later(() => setPhase("quiet"), 3200);
        }, 4000);
      }, 1500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionIdx, agentsDone, phase]);

  const startTour = () => {
    setPhase("tour");
  };

  const highlightAgent = (name) => {
    document.querySelectorAll(".agent-tour-highlight").forEach((c) => {
      c.classList.remove("agent-tour-highlight", "ring-1", "ring-gold/60");
    });
    if (!name) return;
    const slug = name.toLowerCase().replace(/\s+/g, "-");
    const card = document.querySelector(`[data-testid="agent-card-${slug}"]`);
    if (card) card.classList.add("agent-tour-highlight", "ring-1", "ring-gold/60");
  };

  const answerAgent = (yes) => {
    const name = AGENTS[agentIdx];
    if (yes) {
      window.dispatchEvent(
        new CustomEvent("open-concierge", {
          detail: { message: `Tell me more about ${name} — what exactly does it do for my business?` },
        })
      );
    }
    if (agentIdx >= AGENTS.length - 1) {
      highlightAgent(null);
      setAgentsDone(true);
    } else {
      const next = agentIdx + 1;
      setAgentIdx(next);
      highlightAgent(AGENTS[next]);
    }
  };

  const endTour = () => {
    highlightAgent(null);
    setAgentsDone(true);
    sectionRef.current = SECTIONS.length - 1;
    setSectionIdx(SECTIONS.length - 1);
  };

  const farewell = () => {
    highlightAgent(null);
    setAgentsDone(true);
    setPhase("closing");
    later(() => {
      setPhase("return");
      const p = dockPoint();
      if (p) flyToSnake(p, true);
      later(() => setPhase("quiet"), 3400);
    }, 3000);
  };

  useEffect(() => {
    if (phase === "tour" && sectionIdx === 1 && !agentsDone) {
      highlightAgent(AGENTS[agentIdx]);
    } else if (sectionIdx !== 1) {
      highlightAgent(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionIdx, phase, agentsDone]);

  const inWorkforce = phase === "tour" && sectionIdx === 1 && !agentsDone;
  const bubbleAlignClass = align === "right" ? "right-[-6px]" : "left-[-6px]";

  return (
    <>
      <svg
        className="pointer-events-none fixed inset-0 z-30 h-full w-full"
        data-testid="guide-bot-contrail"
        aria-hidden
      >
        <polyline
          ref={trailRef}
          fill="none"
          stroke="rgba(216,192,138,0.45)"
          strokeWidth="3.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ filter: "blur(3px)" }}
        />
        <polyline
          ref={trailCoreRef}
          fill="none"
          stroke="rgba(245,231,193,0.75)"
          strokeWidth="1.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ filter: "blur(0.6px)" }}
        />
      </svg>

      <motion.div
        data-testid="guide-bot"
        style={{ x: sx, y: sy }}
        className="guide-bot pointer-events-none fixed left-0 top-0 z-40"
      >
        <motion.div
          style={{ rotate: sRot, transformOrigin: "50% 16%" }}
        >
          <div
            className="animate-hover pointer-events-auto cursor-pointer"
            data-testid="guide-bot-body"
            onClick={() => {
              if (phase === "quiet" || phase === "docked") setPhase("prompt");
              else if (phase === "tour") farewell();
            }}
            role="button"
            aria-label="Ask the guide for help"
            title={phase === "tour" ? "Send me home" : "Tap me if you need a hand"}
          >
            <BotSvg />
          </div>
        </motion.div>

        <AnimatePresence>
          {phase === "prompt" && (
            <motion.div
              key="prompt"
              data-testid="guide-bot-prompt"
              initial={{ opacity: 0, y: 10, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.9 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className={`glass-panel pointer-events-auto absolute top-[115%] w-56 rounded-2xl border-gold/25 bg-[#0A0A0A]/95 p-4 shadow-[0_20px_50px_rgba(0,0,0,0.7)] ${bubbleAlignClass}`}
            >
              <p className="text-xs leading-relaxed text-[#E8E8E8]">
                Would you like me to explain the site?
              </p>
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  data-testid="guide-bot-yes"
                  onClick={startTour}
                  className="flex-1 rounded-full bg-gold py-1.5 font-mono text-[10px] font-medium uppercase tracking-[0.2em] text-[#050505] transition-colors duration-300 hover:bg-gold-bright"
                >
                  Yes
                </button>
                <button
                  type="button"
                  data-testid="guide-bot-no"
                  onClick={() => setPhase("quiet")}
                  className="flex-1 rounded-full border border-white/15 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#A3A3A3] transition-colors duration-300 hover:border-gold/40 hover:text-gold"
                >
                  No
                </button>
              </div>
            </motion.div>
          )}

          {phase === "tour" && !inWorkforce && (
            <motion.div
              key={`tip-${sectionIdx}`}
              data-testid="guide-bot-tip"
              initial={{ opacity: 0, y: 10, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.9 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className={`glass-panel pointer-events-auto absolute bottom-[115%] w-60 rounded-2xl border-gold/25 bg-[#0A0A0A]/95 p-4 shadow-[0_20px_50px_rgba(0,0,0,0.7)] ${bubbleAlignClass}`}
            >
              <p className="text-xs leading-relaxed text-[#E8E8E8]">
                {SECTIONS[sectionIdx].tip}
              </p>
              <button
                type="button"
                data-testid="guide-bot-end-tour"
                onClick={endTour}
                className="mt-2.5 font-mono text-[9px] uppercase tracking-[0.25em] text-[#A3A3A3] transition-colors duration-300 hover:text-gold"
              >
                End tour
              </button>
            </motion.div>
          )}

          {inWorkforce && (
            <motion.div
              key={`agent-${agentIdx}`}
              data-testid="guide-bot-agent-prompt"
              initial={{ opacity: 0, y: 10, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.9 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className={`glass-panel pointer-events-auto absolute bottom-[115%] w-60 rounded-2xl border-gold/25 bg-[#0A0A0A]/95 p-4 shadow-[0_20px_50px_rgba(0,0,0,0.7)] ${bubbleAlignClass}`}
            >
              <p className="text-xs leading-relaxed text-[#E8E8E8]">
                This is <span className="text-gold">{AGENTS[agentIdx]}</span>.
                Want more detail on this one?
              </p>
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  data-testid="guide-bot-agent-yes"
                  onClick={() => answerAgent(true)}
                  className="flex-1 rounded-full bg-gold py-1.5 font-mono text-[10px] font-medium uppercase tracking-[0.2em] text-[#050505] transition-colors duration-300 hover:bg-gold-bright"
                >
                  Yes
                </button>
                <button
                  type="button"
                  data-testid="guide-bot-agent-no"
                  onClick={() => answerAgent(false)}
                  className="flex-1 rounded-full border border-white/15 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#A3A3A3] transition-colors duration-300 hover:border-gold/40 hover:text-gold"
                >
                  No
                </button>
              </div>
              <button
                type="button"
                data-testid="guide-bot-skip-agents"
                onClick={() => {
                  highlightAgent(null);
                  setAgentsDone(true);
                }}
                className="mt-2.5 font-mono text-[9px] uppercase tracking-[0.25em] text-[#A3A3A3] transition-colors duration-300 hover:text-gold"
              >
                Skip the agents
              </button>
            </motion.div>
          )}

          {phase === "closing" && (
            <motion.div
              key="closing"
              data-testid="guide-bot-closing"
              initial={{ opacity: 0, y: 10, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              className={`glass-panel pointer-events-auto absolute bottom-[115%] w-64 rounded-2xl border-gold/25 bg-[#0A0A0A]/95 p-4 shadow-[0_20px_50px_rgba(0,0,0,0.7)] ${bubbleAlignClass}`}
            >
              <p className="text-xs leading-relaxed text-[#E8E8E8]">
                That's the tour. Thanks for visiting Alkyrvis — the Concierge
                is here if you need anything else.
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </>
  );
}
